import { Room } from "@liveblocks/client";
import { useRoom } from "@liveblocks/react";
import * as Sentry from "@sentry/react";
import { useUpdateAtom } from "jotai/utils";
import { useCallback, useEffect } from "react";
import { presenceAtom } from "../jotai/atoms";
import { Presence, User } from "../types";

export function usePresenceUpdater() {
  const room = useRoom();
  const setPresence = useUpdateAtom(presenceAtom);

  const update = useCallback(() => {
    const p = getPresences(room);
    setPresence(p);
  }, [room, setPresence]);

  useEffect(() => {
    update();

    const unsubscribeSelf = room.subscribe("my-presence", update);
    const unsubscribeOthers = room.subscribe("others", update);

    return () => {
      unsubscribeSelf();
      unsubscribeOthers();
    };
  }, [room, update, setPresence]);
}

function getPresences(room: Room): Record<User["id"], Presence> {
  const others = room.getOthers<Presence>().toArray();
  const self = room.getSelf<Presence>();

  const res: Record<User["id"], Presence> = {};
  for (const user of [self, ...others]) {
    if (user) {
      const { id } = user;
      const presence = validates(user.presence);

      if (id && presence) {
        res[id] = presence;
      }
    }
  }

  return res;
}

/**
 * check if data receive from liveblock contains all the expected
 * Presence fields.
 *
 * It is temporarily patch to the issue that we have observed some required
 * fields were sometimes missing from Liveblock data.
 *
 * @param data - presence data received from Liveblock
 * @returns
 */
function validates(
  data: { [key: string]: unknown } | undefined
): Presence | null {
  if (data && !(data.selection && data.position && data.viewport)) {
    try {
      throw new Error("presence check");
    } catch (e) {
      Sentry.captureException(e, {
        extra: { presence: data },
      });
    }
  }

  if (data && data.selection && data.position && data.viewport) {
    return data as Presence;
  }

  return null;
}
